import { firstValueFrom } from 'rxjs';
import { provideHttpClient } from '@angular/common/http';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, ApplicationConfig, inject, isDevMode } from '@angular/core';
import { PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading, withRouterConfig } from '@angular/router';

import { provideFuse } from '@fuse';
import { appRoutes } from 'app/app.routes';
import { mockApiServices } from 'app/mock-api';
import { provideIcons } from 'app/modules/shared/svg-icons/icons.provider';
import { MatPaginatorLocalized } from './modules/shared/paginators/mat-paginator-localized';
import { TranslocoHttpLoader } from 'app/modules/shared/localization/transloco.http-loader';
import { MatDatepickerLocalized } from './modules/shared/datepickers/mat-datepicker-localized';
import { provideAuthentication } from 'app/modules/shared/authentication/authentication.provider';
import { getBrowserCultureLang, provideTransloco, TranslocoService } from '@jsverse/transloco';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            withRouterConfig({
                resolveNavigationPromiseOnError: true,
                paramsInheritanceStrategy: 'always',
                onSameUrlNavigation: 'reload',
            })
        ),
        provideTransloco({
            config: {
                availableLangs: [
                    { id: 'en-CA', label: 'lang.enCA' },
                    { id: 'en-MP', label: 'lang.enMP' },
                    { id: 'en-US', label: 'lang.enUS' },
                    { id: 'en-VG', label: 'lang.enVG' },
                    { id: 'en-VI', label: 'lang.enVI' },
                    { id: 'es-CO', label: 'lang.esCO' },
                    { id: 'es-ES', label: 'lang.esES' },
                    { id: 'es-MX', label: 'lang.esMX' },
                    { id: 'es-VE', label: 'lang.esVE' },
                ],
                defaultLang: 'en-US',
                prodMode: !isDevMode(),
                reRenderOnLangChange: true,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const browserCultureLang = getBrowserCultureLang();
                const defaultLang = translocoService.getDefaultLang();
                const activeLang = browserCultureLang || defaultLang;

                translocoService.setActiveLang(activeLang);
                return () => firstValueFrom(translocoService.load(activeLang));
            },
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => {
                inject(DateAdapter);
                return () => Promise.resolve();
            },
            deps: [DateAdapter],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => {
                inject(MatDatepickerIntl);
                return () => Promise.resolve();
            },
            deps: [MatDatepickerIntl],
            multi: true,
        },
        {
            provide: MatDatepickerIntl,
            useClass: MatDatepickerLocalized,
        },
        {
            provide: MatPaginatorIntl,
            useClass: MatPaginatorLocalized,
        },
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },
        provideAuthentication(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'classy',
                scheme: 'auto',
                screens: {
                    sm: '600px',
                    md: '960px',
                    ml: '1120px',
                    lg: '1280px',
                    lx: '1390px',
                    xl: '1440px',
                },
                theme: 'theme-brand',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
